import { createStyles, makeStyles } from "@mui/styles";
import { useSegments } from "api/segments";
import { InfoBox } from "components/Layout/InfoBox";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useNavigate } from "react-router-dom";
import { links } from "utilities/links";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    deactivateInfo: {
      color: theme.gray500,
      fontSize: "14px",
      marginTop: 0,
    },
    infoBox: {
      marginBottom: theme.spacing(12),
    },
  }),
);

export function DeactivateSegmentModal({
  open,
  segmentId,
  segmentName,
  prevPath,
  close,
}: {
  open: boolean;
  segmentId: string;
  segmentName?: string;
  prevPath: string;
  close: () => void;
}) {
  const classes = useStyles();
  const navigate = useNavigate();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { deactivateSegment } = useSegments({ segmentId });

  const {
    useSegmentAssociatedCampaigns,
    useSegmentAssociatedRewards,
    useSegmentAssociatedAchievements,
  } = useSegments();

  const { data: campaignsData } = useSegmentAssociatedCampaigns(
    segmentId as string,
  );

  const { data: rewardsData } = useSegmentAssociatedRewards(
    segmentId as string,
  );

  const { data: achievementsData } = useSegmentAssociatedAchievements(
    segmentId as string,
  );

  const handleDeactivateSegment = async () => {
    const associatedItemsExist =
      (campaignsData && campaignsData.items.length > 0) ||
      (rewardsData && rewardsData.items.length > 0) ||
      (achievementsData && achievementsData.items.length > 0);

    if (associatedItemsExist) {
      navigate(links.deactivatedSegment(segmentId as string), {
        state: { prevPath },
      });
    } else {
      const response = await deactivateSegment(segmentId);
      if (response) {
        close();
        notify(message({ id: "segmentDeactivated" }));
        navigate(prevPath);
      }
    }
  };

  return (
    <ConfirmationModal
      open={open}
      submitText="yesDeactivate"
      modalWidth={536}
      close={close}
      cancelText={message({ id: "cancel" })}
      data-test-id="deactivate-modal"
      action={handleDeactivateSegment}
      header={
        <>
          {message(
            { id: "areYouSureYouWantToDeactivateSegment" },
            { name: segmentName },
          )}
        </>
      }
      info={
        <>
          <p className={classes.deactivateInfo}>
            {message({ id: "deactivateSegmentInfo" })}
          </p>
          <InfoBox
            type="info"
            className={classes.infoBox}
            text={message({ id: "deactivateSegmentInfoBox" })}
          />
        </>
      }
    />
  );
}
